/**
 * API klienti.
 *
 * Barcha so'rovlar `/api/v1` ga ketadi; dev da Vite proxysi uni `apps/api` ga
 * uzatadi (`vite.config.ts`), prodda web va API bitta domenda bo'ladi — ya'ni
 * bazaviy manzil kodga qotirilmaydi.
 *
 * TOKENLAR (PP-1211/1213): access token qisqa umrli (15 daq) va `localStorage`
 * da; muddati tugaganda refresh token bilan JIMGINA yangilanadi va so'rov bir
 * marta qaytariladi. Foydalanuvchi har 15 daqiqada qaytadan kirmaydi, sessiyani
 * uzoqdan yakunlash esa eng ko'p 15 daqiqada kuchga kiradi.
 *
 * TODO(PP-1213): `httpOnly` cookie ga o'tish — sessiya siyosati tasdiqlangach.
 */

import type {
  Analysis,
  AnalyticsActivityResponse,
  AnalyticsCalibrationResponse,
  AnalyticsCustomersResponse,
  AnalyticsLeadsResponse,
  AnalyticsOverviewResponse,
  AnalyticsQualityResponse,
  AnalyticsTeamSkillsResponse,
  AssistantChatResponse,
  AssistantMessage,
  Call,
  CrmConnectionRequest,
  Employee,
  LeadDetailResponse,
  LeadInsightsResponse,
  PulseErrorPayload,
  RubricPutRequest,
  StatusResponse,
  TranscriptSegment,
} from "@pulse/shared";

const ACCESS_KEY = "pulse.access";
const REFRESH_KEY = "pulse.refresh";

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function setTokens(access: string | null, refresh?: string | null): void {
  if (access === null) {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    return;
  }

  localStorage.setItem(ACCESS_KEY, access);

  if (refresh) {
    localStorage.setItem(REFRESH_KEY, refresh);
  }
}

/** API xatosi — foydalanuvchiga ko'rinadigan matn bilan (NF-405/602). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * NF-506 — so'rov identifikatori.
   * Foydalanuvchi qo'llab-quvvatlashga aynan shuni aytadi; jurnal shu bo'yicha
   * qidiriladi. Shuning uchun u xato panelida KO'RSATILADI.
   */
  readonly traceId: string | null;

  constructor(status: number, code: string, message: string, traceId: string | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.traceId = traceId;
  }
}

/** Access tokenni refresh token bilan yangilaydi. */
async function refreshAccess(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);

  if (!refreshToken) {
    return false;
  }

  const response = await fetch("/api/v1/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    setTokens(null);
    return false;
  }

  const body = (await response.json()) as { accessToken?: string };

  if (!body.accessToken) {
    return false;
  }

  localStorage.setItem(ACCESS_KEY, body.accessToken);
  return true;
}

async function request<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const token = getAccessToken();

  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  // 401 — access token eskirgan bo'lishi mumkin: bir marta yangilab ko'riladi.
  if (response.status === 401 && !retried && (await refreshAccess())) {
    return request<T>(path, init, true);
  }

  if (!response.ok) {
    // Server HAR xatoni `PulseErrorPayload` shaklida qaytaradi
    // (`apps/api/src/common/exception.filter.ts`). Shu sababli bu yerda
    // uch xil shaklni taxmin qilish shart emas.
    const body = (await response.json().catch(() => ({}))) as Partial<PulseErrorPayload>;

    // Tarmoq uzilgan yoki proksi HTML qaytargan holat: javob JSON emas va
    // `body` bo'sh qoladi. O'shanda ham foydalanuvchi tushunarli matn oladi.
    throw new ApiError(
      response.status,
      body.code ?? "INTERNAL",
      body.message ?? "So'rov bajarilmadi. Internet aloqasini tekshiring.",
      body.traceId ?? response.headers.get("X-Trace-Id"),
    );
  }

  return (await response.json()) as T;
}

/**
 * Multipart so'rov (PP-105 — qo'lda audio yuklash).
 *
 * `Content-Type` ATAYLAB qo'yilmaydi: brauzer `FormData` uchun boundary bilan
 * o'zi yozadi; qo'lda yozilsa server multipartni o'qiy olmaydi.
 */
async function requestMultipart<T>(path: string, form: FormData, retried = false): Promise<T> {
  const token = getAccessToken();

  const response = await fetch(`/api/v1${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  if (response.status === 401 && !retried && (await refreshAccess())) {
    return requestMultipart<T>(path, form, true);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Partial<PulseErrorPayload>;
    throw new ApiError(
      response.status,
      body.code ?? "INTERNAL",
      body.message ?? "Fayl yuklanmadi. Internet aloqasini tekshiring.",
      body.traceId ?? response.headers.get("X-Trace-Id"),
    );
  }

  return (await response.json()) as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tiplar (server javoblari)
// ─────────────────────────────────────────────────────────────────────────────

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly role: string;
  readonly businessId: string;
  readonly businessName: string;
  readonly employeeId: string | null;
  readonly businesses: readonly { id: string; name: string; role: string }[];
}

export interface CallListItem {
  readonly call: Call;
  readonly employeeName: string | null;
  readonly score: number | null;
  readonly isManualScore: boolean;
  readonly isScorable: boolean | null;
  readonly isLowConfidence: boolean;
  readonly isSuspicious: boolean;
  readonly callFamilyCode: string | null;
}

export interface CriterionMeta {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly categoryId: string;
  readonly descriptors: readonly string[];
}

export interface CallDetail {
  readonly call: Call;
  readonly employeeName: string | null;
  readonly analysis: Analysis | null;
  readonly segments: readonly TranscriptSegment[];
  readonly transcriptMeta: {
    sttProvider: string;
    sttModel: string;
    rtf: number | null;
    diarizationMethod: string;
    speakersDetected: number | null;
    needsManualReview: boolean;
  } | null;
  readonly categoryNames: Record<string, string>;
  readonly criteria: readonly CriterionMeta[];
  readonly redFlagNames: Record<string, string>;
  readonly questionTexts: Record<string, string>;
  readonly statusEvents: readonly {
    toStatus: string;
    stage: string | null;
    errorCode: string | null;
    error: string | null;
    createdAt: string;
  }[];
}

/** Davr taqqoslashli metrika (Ilova C). */
export interface MetricComparison {
  readonly current: number | null;
  readonly previous: number | null;
  readonly changePercent: number | null;
  readonly isNew: boolean;
}

export interface DashboardResponse {
  readonly periodDays: number;
  readonly pulse: {
    status: "stable" | "attention" | "risk";
    text: string;
    reasons: readonly string[];
  };
  readonly counters: {
    calls: number;
    analyzed: number;
    salesCalls: number;
    operationalCalls: number;
    suspicious: number;
    lowConfidence: number;
    pending: number;
    failed: number;
  };
  readonly metrics: {
    conversationQuality: MetricComparison;
    outboundConnectRate: MetricComparison;
    averageCallDuration: MetricComparison;
    conversionRate: MetricComparison;
  };
  readonly statusCounts: Record<string, number>;
  readonly leaderboard: readonly {
    employeeId: string;
    fullName: string;
    seatStatus: string;
    callsCount: number;
    salesCallsCount: number;
    quality: number | null;
    avgDurationSec: number | null;
    missedInbound: number;
  }[];
  readonly criterionTrend: readonly {
    criterionId: string;
    code: string;
    name: string;
    categoryName: string;
    avgScore: number;
    percent: number;
    weakCount: number;
    scoredCount: number;
  }[];
  readonly redFlagAudit: readonly { redFlagDefinitionId: string; name: string; count: number }[];
  /** PP-603 — CRM faolligi: yaratilgan lidlar, yutilgan bitimlar, kunlik dinamika. */
  readonly crmActivity: {
    newLeadsCount: number;
    wonDealsCount: number;
    wonAmountUzs: number;
    series: readonly {
      localDate: string;
      newLeadsCount: number;
      wonDealsCount: number;
      wonAmountUzs: number;
    }[];
  };
}

/** PP-414 — qidiruvda topilgan segment parchasi (har qo'ng'iroqqa bittadan). */
export interface SearchSnippet {
  readonly callId: string;
  readonly segmentId: string;
  readonly startMs: number;
  readonly text: string;
}

/** PP-412 — ulashilgan qo'ng'iroqning o'qish-only ko'rinishi (PII minimal). */
export interface SharedCallView {
  readonly callId: string;
  readonly startedAt: string;
  readonly durationSec: number | null;
  readonly direction: string;
  readonly phoneMasked: string | null;
  readonly employeeName: string | null;
  readonly score: number | null;
  readonly callFamilyCode: string | null;
  readonly summary: Record<string, unknown> | null;
  readonly categoryScores: readonly {
    categoryId: string;
    percent: number;
    comment: string | null;
  }[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Endpointlar
// ─────────────────────────────────────────────────────────────────────────────

export const api = {
  /**
   * Kirish (PP-1211).
   *
   * IKKI XIL JAVOB bo'lishi mumkin:
   *   - sessiya (token + foydalanuvchi) — 2FA yoqilmagan;
   *   - `{ mfaRequired: true, challengeToken }` — ikkinchi omil kerak (PP-1212).
   * Chaqiruvchi ikkalasini ham qayta ishlashi SHART: aks holda 2FA yoqqan
   * foydalanuvchi «bo'sh ekran» oladi.
   */
  async login(
    email: string,
    password: string,
    businessId?: string,
  ): Promise<
    | { accessToken: string; refreshToken: string; user: SessionUser; mfaRequired?: false }
    | { mfaRequired: true; challengeToken: string }
  > {
    return request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, ...(businessId ? { businessId } : {}) }),
    });
  },

  async me(): Promise<SessionUser> {
    return request("/auth/me");
  },

  async switchBusiness(businessId: string): Promise<{ accessToken: string }> {
    return request("/auth/switch-business", {
      method: "POST",
      body: JSON.stringify({ businessId }),
    });
  },

  async logout(): Promise<void> {
    await request("/auth/logout", { method: "POST" });
  },

  async sessions(): Promise<{
    items: { id: string; ip: string | null; userAgent: string | null; lastSeenAt: string }[];
  }> {
    return request("/auth/sessions");
  },

  async calls(params: {
    limit?: number;
    offset?: number;
    status?: string;
    employeeId?: string;
    direction?: string;
    family?: string;
    /** PP-414 — matnli qidiruv (kamida 2 belgi). */
    q?: string;
  }): Promise<{ items: CallListItem[]; total: number; snippets?: SearchSnippet[] }> {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") {
        query.set(key, String(value));
      }
    }

    return request(`/calls?${query.toString()}`);
  },

  async call(id: string): Promise<CallDetail> {
    return request(`/calls/${id}`);
  },

  async retryCall(id: string): Promise<{ status: string }> {
    return request(`/calls/${id}/retry`, { method: "POST" });
  },

  async disputeCall(id: string, reason: string): Promise<{ status: string }> {
    return request(`/calls/${id}/dispute`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  async adjustScore(id: string, score: number, reason: string): Promise<{ score: number }> {
    return request(`/calls/${id}/adjust-score`, {
      method: "POST",
      body: JSON.stringify({ score, reason }),
    });
  },

  async sendFeedback(id: string, verdict: "ok" | "wrong", comment?: string): Promise<void> {
    await request(`/calls/${id}/feedback`, {
      method: "POST",
      body: JSON.stringify({ verdict, comment }),
    });
  },

  /** PP-205 — transkript segment matnini tuzatish (owner/rop). */
  async editSegment(
    callId: string,
    segmentId: string,
    text: string,
  ): Promise<{ status: string; textOriginal: string }> {
    return request(`/calls/${callId}/segments/${segmentId}`, {
      method: "PATCH",
      body: JSON.stringify({ text }),
    });
  },

  /** PP-412 — muddatli (7 kun) ulashish havolasi. */
  async shareCall(callId: string): Promise<{ token: string; expiresAt: string }> {
    return request(`/calls/${callId}/share`, { method: "POST" });
  },

  /**
   * PP-401 — audio oqimi uchun qisqa umrli (10 daq) token.
   * `<audio src>` sarlavha yubora olmagani uchun token URL da yuboriladi.
   */
  async audioToken(callId: string): Promise<{ token: string; expiresAt: string }> {
    return request(`/calls/${callId}/audio-token`, { method: "POST" });
  },

  /** PP-412 — ulashilgan ko'rinish. AUTHSIZ ham ishlaydi (token — URL da). */
  async sharedCall(token: string): Promise<SharedCallView> {
    return request(`/shared/${encodeURIComponent(token)}`);
  },

  /** PP-401 — `<audio>` uchun src (tokenli). */
  audioUrl(callId: string, token: string): string {
    return `/api/v1/calls/${callId}/audio?t=${encodeURIComponent(token)}`;
  },

  async dashboard(days: number): Promise<DashboardResponse> {
    return request(`/dashboard?days=${String(days)}`);
  },

  async employees(): Promise<{ items: Employee[] }> {
    return request("/employees");
  },

  /** PP-1204 — yangi o'rindiq/xodim. */
  async createEmployee(body: {
    fullName: string;
    email?: string | null;
    crmUserId?: string | null;
    departmentId?: string | null;
    branchId?: string | null;
  }): Promise<{ status: string; id: string }> {
    return request("/employees", { method: "POST", body: JSON.stringify(body) });
  },

  /** PP-1217 — o'rindiqni arxivlash. */
  async archiveEmployee(id: string): Promise<{ status: string }> {
    return request(`/employees/${id}/archive`, { method: "POST" });
  },

  /** PP-1203 — taklifni qabul qilish (authsiz — hali login yo'q). */
  async acceptInvite(token: string, password: string): Promise<{ status: string; email: string }> {
    return request("/team/accept-invite", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Jamoa (PP-1203) — faqat owner
// ─────────────────────────────────────────────────────────────────────────────

export interface TeamMemberItem {
  readonly membershipId: string;
  readonly userId: string;
  readonly email: string;
  readonly fullName: string;
  readonly role: string;
  readonly status: string;
  readonly employeeId: string | null;
  readonly lastLoginAt: string | null;
  readonly invitedAt: string;
}

export const teamApi = {
  async members(): Promise<{ items: TeamMemberItem[] }> {
    return request("/team/members");
  },

  async invite(body: {
    email: string;
    fullName: string;
    role: string;
  }): Promise<{ status: string; membershipId: string }> {
    return request("/team/members", { method: "POST", body: JSON.stringify(body) });
  },

  async update(
    membershipId: string,
    patch: { role?: string; status?: "active" | "blocked" },
  ): Promise<{ status: string }> {
    return request(`/team/members/${membershipId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Sozlamalar (PP-1201–1209)
// ─────────────────────────────────────────────────────────────────────────────

/** Sozlamalar sahifasi javobi — `packages/db` dagi `BusinessSettingsView`. */
export interface BusinessSettings {
  readonly id: string;
  readonly name: string;
  readonly industry: string | null;
  readonly description: string | null;
  readonly language: string;
  readonly timezone: string;
  readonly retentionMonths: number;
  readonly coldStorageAfterMonths: number | null;
  readonly shortCallThresholdSec: number;
  readonly lowConfidenceThreshold: number;
  readonly conversionPipelines: readonly string[];
  readonly tasksEnabled: boolean;
  /** NF-402 — diarizatsiya rejimi. */
  readonly diarizationMode: "auto" | "force_on" | "force_off";
  readonly mfaRequired: boolean;
  readonly maxConcurrentAnalyses: number;
  readonly dailyLimitCalls: number | null;
  readonly dailyLimitMinutes: number | null;
  readonly status: string;
  readonly workSchedule: readonly {
    weekday: number;
    startTime: string;
    endTime: string;
    isWorking: boolean;
  }[];
}

export interface RubricView {
  readonly id: string;
  readonly rubricName: string;
  readonly versionNo: number;
  readonly status: string;
  readonly publishedAt: string | null;
  readonly changeNote: string | null;
  readonly maxCriterionScore: number;
  readonly totalWeight: number;
  readonly categories: readonly {
    id: string;
    code: string;
    name: string;
    weightPercent: number;
    criteria: readonly {
      id: string;
      code: string;
      name: string;
      description: string | null;
      descriptors: readonly { score: number; descriptor: string }[];
    }[];
  }[];
  readonly redFlags: readonly {
    id: string;
    code: string;
    name: string;
    description: string | null;
    penaltyPoints: number;
    isGamingPattern: boolean;
    alertImmediately: boolean;
  }[];
  readonly questions: readonly { id: string; code: string; text: string }[];
  readonly aiContext: {
    versionNo: number;
    companyContext: string | null;
    productNames: readonly string[];
    forbiddenPromises: readonly string[];
    glossary: Record<string, string>;
    publishedAt: string | null;
  } | null;
}

export interface CallFamily {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly isScorable: boolean;
}

export const settingsApi = {
  async read(): Promise<BusinessSettings> {
    return request("/settings");
  },

  async update(patch: Record<string, unknown>): Promise<{ changed: string[] }> {
    return request("/settings", { method: "PUT", body: JSON.stringify(patch) });
  },

  async rubric(): Promise<RubricView> {
    return request("/settings/rubric");
  },

  async rubricVersions(): Promise<{
    items: {
      id: string;
      versionNo: number;
      status: string;
      publishedAt: string | null;
      changeNote: string | null;
      isCurrent: boolean;
    }[];
  }> {
    return request("/settings/rubric/versions");
  },

  async callFamilies(): Promise<{ items: CallFamily[] }> {
    return request("/settings/call-families");
  },

  async org(): Promise<{
    branches: { id: string; name: string }[];
    departments: { id: string; name: string; branchId: string | null }[];
  }> {
    return request("/settings/org");
  },

  /** PP-311–314 — rubrikaning YANGI versiyasini chop etish. */
  async saveRubric(
    body: RubricPutRequest,
  ): Promise<{ status: string; versionId: string; versionNo: number }> {
    return request("/settings/rubric", { method: "PUT", body: JSON.stringify(body) });
  },

  /** PP-1216 — sarf paneli (jami + kunlik seriya). */
  async usage(days: number): Promise<UsageSeriesResult> {
    return request(`/settings/usage?days=${String(days)}`);
  },

  /**
   * PP-110 — retro-tahlil: oxirgi N kundagi tayyor qo'ng'iroqlarni yangi
   * rubrika versiyasi bilan qayta baholashga navbatga qo'yadi (owner).
   */
  async reanalyze(days: number): Promise<{ status: string; requeued: number; days: number }> {
    return request("/settings/reanalyze", { method: "POST", body: JSON.stringify({ days }) });
  },

  /** PP-110 — retro holati va sarf ogohlantirishi (nechta qo'ng'iroq kiradi). */
  async reanalyzeStatus(
    days: number,
  ): Promise<{ running: boolean; candidates: number; days: number }> {
    return request(`/settings/reanalyze?days=${String(days)}`);
  },

  /** PP-317 — AI ko'rsatmalari: amaldagi versiya va tarix. */
  async aiInstructions(): Promise<{
    current: AiInstructionVersion | null;
    history: AiInstructionVersion[];
  }> {
    return request("/settings/ai-instructions");
  },

  /** PP-317 — AI ko'rsatmasining yangi versiyasini saqlash (eski saqlanadi). */
  async saveAiInstructions(body: {
    companyContext: string | null;
    productNames: string[];
    forbiddenPromises: string[];
    glossary: Record<string, string>;
  }): Promise<{ status: string; versionNo: number }> {
    return request("/settings/ai-instructions", { method: "PUT", body: JSON.stringify(body) });
  },

  /** PP-904 — ogohlantirish qoidalari ro'yxati (owner/rop). */
  async alertRules(): Promise<{ items: AlertRuleItem[] }> {
    return request("/settings/alert-rules");
  },

  /** PP-904 — qoidalarni saqlash: yoqish/o'chirish va chegara. */
  async saveAlertRules(
    items: { id: string; enabled: boolean; threshold: Record<string, number | boolean> }[],
  ): Promise<{ status: string; updated: number }> {
    return request("/settings/alert-rules", { method: "PUT", body: JSON.stringify({ items }) });
  },

  /** PP-1210 — audit jurnali (faqat owner). */
  async audit(params: {
    limit?: number;
    offset?: number;
    action?: string;
  }): Promise<{ items: AuditLogItem[]; total: number }> {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") {
        query.set(key, String(value));
      }
    }

    return request(`/settings/audit?${query.toString()}`);
  },

  /** PP-1219 — yangi eksport so'rovi. */
  async requestExport(
    kind: "calls_xlsx" | "employee_xlsx" | "full_data",
    piiMode: "masked" | "full",
  ): Promise<{ status: string; id: string }> {
    return request("/settings/export", { method: "POST", body: JSON.stringify({ kind, piiMode }) });
  },

  /** PP-1219 — eksport so'rovlari ro'yxati. */
  async exports(): Promise<{ items: ExportJobItem[] }> {
    return request("/settings/export");
  },

  /** PP-1219 — tayyor eksport faylini yuklab olib, brauzerda saqlaydi. */
  async downloadExport(id: string, filename: string): Promise<void> {
    const token = getAccessToken();
    const response = await fetch(`/api/v1/settings/export/${id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as Partial<PulseErrorPayload>;
      throw new ApiError(
        response.status,
        body.code ?? "INTERNAL",
        body.message ?? "Eksport yuklab olinmadi",
        body.traceId ?? null,
      );
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
};

/** PP-317 — AI ko'rsatmasining bitta versiyasi. */
export interface AiInstructionVersion {
  readonly versionNo: number;
  readonly companyContext: string | null;
  readonly productNames: readonly string[];
  readonly forbiddenPromises: readonly string[];
  readonly glossary: Record<string, string>;
  readonly publishedAt: string | null;
}

/** PP-904 — ogohlantirish qoidasi qatori. */
export interface AlertRuleItem {
  readonly id: string;
  readonly type: string;
  readonly enabled: boolean;
  readonly threshold: Record<string, unknown>;
  readonly recipientRole: string | null;
  readonly channels: readonly string[];
}

/** PP-1216 — kunlik sarf qatori. */
export interface UsageDayRow {
  readonly localDate: string;
  readonly callsAnalyzed: number;
  readonly sttSeconds: number;
  readonly sttComputeSeconds: number;
  readonly llmInputTokens: number;
  readonly llmOutputTokens: number;
}

export interface UsageSeriesResult {
  readonly days: number;
  readonly totals: {
    readonly callsAnalyzed: number;
    readonly sttSeconds: number;
    readonly sttComputeSeconds: number;
    readonly llmInputTokens: number;
    readonly llmOutputTokens: number;
  };
  readonly series: readonly UsageDayRow[];
}

/** PP-1210 — audit jurnali yozuvi. */
export interface AuditLogItem {
  readonly id: string;
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string | null;
  readonly actorRole: string | null;
  readonly actorName: string | null;
  readonly before: unknown;
  readonly after: unknown;
  readonly ip: string | null;
  readonly createdAt: string;
}

/** PP-1219 — eksport so'rovi holati. */
export interface ExportJobItem {
  readonly id: string;
  readonly kind: "calls_xlsx" | "employee_xlsx" | "full_data";
  readonly piiMode: "masked" | "full";
  readonly status: "queued" | "running" | "done" | "failed";
  readonly rowCount: number | null;
  readonly error: string | null;
  readonly expiresAt: string | null;
  readonly createdAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Vazifalar — M10 (PP-1001–1005)
// ─────────────────────────────────────────────────────────────────────────────

/** Vazifa qatori — `packages/db` dagi `TaskItem` bilan bir xil. */
export interface TaskItem {
  readonly id: string;
  readonly employeeId: string | null;
  readonly employeeName: string | null;
  /** `ai` — quvur yaratgan (PP-1001), `manual` — ROP kouchingi (PP-1004). */
  readonly origin: string;
  readonly callId: string | null;
  readonly text: string;
  readonly dueAt: string | null;
  readonly status: string;
  readonly doneAt: string | null;
  readonly isOverdue: boolean;
  readonly createdAt: string;
}

export const tasksApi = {
  async list(params: {
    limit?: number;
    offset?: number;
    status?: string;
    employeeId?: string;
    overdue?: boolean;
  }): Promise<{ items: TaskItem[]; total: number; overdueCount: number }> {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "" && value !== false) {
        query.set(key, String(value));
      }
    }

    return request(`/tasks?${query.toString()}`);
  },

  /** PP-1004 — kouching vazifasi (owner/rop). */
  async create(body: {
    employeeId: string;
    text: string;
    dueAt?: string;
    callId?: string;
  }): Promise<{ status: string; id: string }> {
    return request("/tasks", { method: "POST", body: JSON.stringify(body) });
  },

  /** PP-1003 — bajarildi/bekor/qayta ochish. */
  async setStatus(id: string, status: "open" | "done" | "cancelled"): Promise<{ status: string }> {
    return request(`/tasks/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Hisobotlar — M8 (PP-801/803/805)
// ─────────────────────────────────────────────────────────────────────────────

export interface ReportSummary {
  readonly id: string;
  readonly reportDate: string;
  readonly kind: string;
  readonly status: string;
  readonly generatedAt: string;
  readonly sentAt: string | null;
}

export interface ReportDetail extends ReportSummary {
  readonly metrics: Record<string, unknown>;
  readonly narrative: string | null;
}

export interface DailyReportSettingsView {
  readonly sendTime: string;
  readonly recipients: readonly string[];
  readonly enabled: boolean;
}

export const reportsApi = {
  async list(params: {
    kind?: "daily" | "weekly";
    limit?: number;
    offset?: number;
  }): Promise<{ items: ReportSummary[]; total: number }> {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        query.set(key, String(value));
      }
    }

    return request(`/reports?${query.toString()}`);
  },

  async detail(id: string): Promise<ReportDetail> {
    return request(`/reports/${id}`);
  },

  /** PP-803 — CSV matni (blob sifatida yuklab olinadi). */
  async csv(id: string): Promise<string> {
    const token = getAccessToken();
    const response = await fetch(`/api/v1/reports/${id}/csv`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new ApiError(response.status, "INTERNAL", "CSV yuklab olinmadi");
    }

    return response.text();
  },

  async dailySettings(): Promise<DailyReportSettingsView | { enabled: false }> {
    return request("/settings/daily-report");
  },

  async saveDailySettings(body: {
    enabled: boolean;
    sendTime: string;
    recipients: string[];
  }): Promise<{ status: string }> {
    return request("/settings/daily-report", { method: "PUT", body: JSON.stringify(body) });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Signallar (PP-323/327/331/415)
// ─────────────────────────────────────────────────────────────────────────────

export type SignalKind = "red_flag" | "gaming" | "dispute" | "failed" | "low_confidence";

export interface SignalItem {
  readonly kind: SignalKind;
  readonly callId: string;
  readonly callStartedAt: string;
  readonly employeeName: string | null;
  readonly direction: string;
  readonly durationSec: number | null;
  readonly score: number | null;
  readonly title: string;
  readonly detail: string | null;
  readonly occurredAt: string;
  readonly resolved: boolean;
}

export interface DailyRow {
  readonly localDate: string;
  readonly calls: number;
  readonly analyzed: number;
  readonly avgScore: number | null;
  readonly redFlags: number;
  readonly suspicious: number;
}

export const signalsApi = {
  async list(days: number): Promise<{
    items: SignalItem[];
    counts: Record<SignalKind, number>;
  }> {
    return request(`/signals?days=${String(days)}`);
  },

  async daily(days: number): Promise<{ items: DailyRow[] }> {
    return request(`/signals/daily?days=${String(days)}`);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Ikki bosqichli tasdiqlash (PP-1212)
// ─────────────────────────────────────────────────────────────────────────────

export interface MfaStatus {
  readonly enrolled: boolean;
  readonly confirmed: boolean;
  readonly recoveryCodesLeft: number;
}

export const mfaApi = {
  async status(): Promise<MfaStatus> {
    return request("/auth/mfa");
  },

  async start(): Promise<{ secret: string; otpauthUrl: string }> {
    return request("/auth/mfa/start", { method: "POST" });
  },

  async confirm(code: string): Promise<{ status: string; recoveryCodes: string[] }> {
    return request("/auth/mfa/confirm", { method: "POST", body: JSON.stringify({ code }) });
  },

  async disable(password: string): Promise<{ status: string }> {
    return request("/auth/mfa/disable", { method: "POST", body: JSON.stringify({ password }) });
  },

  /** Kirishning ikkinchi qadami — TOKENSIZ chaqiriladi. */
  async challenge(
    challengeToken: string,
    code: string,
  ): Promise<{ accessToken: string; refreshToken: string; user: SessionUser }> {
    return request("/auth/mfa", {
      method: "POST",
      body: JSON.stringify({ challengeToken, code }),
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Bildirishnomalar (PP-905)
// ─────────────────────────────────────────────────────────────────────────────

export interface NotificationItem {
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly body: string | null;
  readonly entityType: string | null;
  readonly entityId: string | null;
  readonly readAt: string | null;
  readonly createdAt: string;
}

export interface NotificationPreference {
  readonly type: string;
  readonly inApp: boolean;
  readonly email: boolean;
  /** Rol uchun majburiy — foydalanuvchi o'chira olmaydi (PP-905). */
  readonly mandatory: boolean;
}

export const notificationsApi = {
  async list(options: { limit?: number; unread?: boolean; type?: string } = {}): Promise<{
    items: NotificationItem[];
    unreadCount: number;
  }> {
    const query = new URLSearchParams();

    if (options.limit !== undefined) query.set("limit", String(options.limit));
    if (options.unread === true) query.set("unread", "true");
    if (options.type !== undefined) query.set("type", options.type);

    return request(`/notifications?${query.toString()}`);
  },

  /** `ids` bo'sh — hammasini o'qildi deb belgilaydi. */
  async markRead(ids: string[] = []): Promise<{ updated: number }> {
    return request("/notifications/read", { method: "POST", body: JSON.stringify({ ids }) });
  },

  async preferences(): Promise<{ items: NotificationPreference[] }> {
    return request("/notifications/preferences");
  },

  async updatePreference(
    type: string,
    inApp: boolean,
    email: boolean,
  ): Promise<{ applied: boolean; reason?: string }> {
    return request("/notifications/preferences", {
      method: "PATCH",
      body: JSON.stringify({ type, inApp, email }),
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Analitika (PP-611–618)
// ─────────────────────────────────────────────────────────────────────────────

export const analyticsApi = {
  async overview(days: number): Promise<AnalyticsOverviewResponse> {
    return request(`/analytics/overview?days=${String(days)}`);
  },

  async quality(days: number): Promise<AnalyticsQualityResponse> {
    return request(`/analytics/quality?days=${String(days)}`);
  },

  async teamSkills(days: number): Promise<AnalyticsTeamSkillsResponse> {
    return request(`/analytics/team-skills?days=${String(days)}`);
  },

  async customers(days: number): Promise<AnalyticsCustomersResponse> {
    return request(`/analytics/customers?days=${String(days)}`);
  },

  async activity(days: number): Promise<AnalyticsActivityResponse> {
    return request(`/analytics/activity?days=${String(days)}`);
  },

  async leads(days: number): Promise<AnalyticsLeadsResponse> {
    return request(`/analytics/leads?days=${String(days)}`);
  },

  /** PP-334 — kalibrlash: AI ball vs qo'lda tuzatilgan ball. */
  async calibration(days: number): Promise<AnalyticsCalibrationResponse> {
    return request(`/analytics/calibration?days=${String(days)}`);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Ochiq status sahifa (NF-505) — authsiz
// ─────────────────────────────────────────────────────────────────────────────

export const statusApi = {
  /** NF-505 — xizmatlar holati; token bo'lmasa ham ishlaydi. */
  async read(): Promise<StatusResponse> {
    return request("/status");
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Lid xulosalari (M5, PP-501–505)
// ─────────────────────────────────────────────────────────────────────────────

export const leadsApi = {
  async insights(params: {
    status: "lost" | "at_risk" | "all";
    days: number;
    page: number;
  }): Promise<LeadInsightsResponse> {
    const query = new URLSearchParams({
      status: params.status,
      days: String(params.days),
      page: String(params.page),
    });
    return request(`/leads/insights?${query.toString()}`);
  },

  async detail(id: string): Promise<LeadDetailResponse> {
    return request(`/leads/${id}`);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// AI yordamchi (M7, PP-701–704)
// ─────────────────────────────────────────────────────────────────────────────

export const assistantApi = {
  /** Stateless suhbat: tarix kliyentdan, oxirgi 10 xabar (PP-703). */
  async chat(messages: AssistantMessage[], period?: number): Promise<AssistantChatResponse> {
    return request("/assistant/chat", {
      method: "POST",
      body: JSON.stringify({ messages, ...(period === undefined ? {} : { period }) }),
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CRM ulanish va ingest (PP-101, PP-105, PP-108, PP-1206)
// ─────────────────────────────────────────────────────────────────────────────

/** PP-1206 — CRM ulanish holati (token QAYTMAYDI, NF-302). */
export type CrmConnectionStatus =
  | { connected: false }
  | {
      connected: true;
      id: string;
      provider: string;
      portalUrl: string;
      status: string;
      lastSyncAt: string | null;
      lastError: string | null;
    };

export interface IngestKeyItem {
  readonly id: string;
  readonly name: string;
  readonly lastUsedAt: string | null;
  readonly createdAt: string;
}

export const crmApi = {
  async read(): Promise<CrmConnectionStatus> {
    return request("/settings/crm-connection");
  },

  async save(body: CrmConnectionRequest): Promise<{ status: string; connectionId: string }> {
    return request("/settings/crm-connection", { method: "PUT", body: JSON.stringify(body) });
  },

  async test(): Promise<{ status: string; message?: string }> {
    return request("/settings/crm-connection/test", { method: "POST" });
  },

  async ingestKeys(): Promise<{ items: IngestKeyItem[] }> {
    return request("/settings/ingest-keys");
  },

  /** Ochiq kalit javobda BIR MARTA keladi — qayta so'rab bo'lmaydi (NF-302). */
  async createIngestKey(name: string): Promise<{ status: string; id: string; apiKey: string }> {
    return request("/settings/ingest-keys", { method: "POST", body: JSON.stringify({ name }) });
  },
};

export const ingestApi = {
  /** PP-105 — qo'lda audio yuklash (multipart). */
  async upload(
    file: File,
    metadata: { employeeId?: string; direction?: string; startedAt?: string },
  ): Promise<{ status: string; callId: string }> {
    const form = new FormData();
    form.append("file", file);

    for (const [key, value] of Object.entries(metadata)) {
      if (value !== undefined && value !== "") {
        form.append(key, value);
      }
    }

    return requestMultipart("/ingest/upload", form);
  },
};
